import { motion } from "motion/react";
import { ShieldCheck, Upload, Camera, CheckCircle2, AlertCircle } from "lucide-react";
import { useState } from "react";

import { useRWAStore } from "../store/rwa-store";

export default function KycView() {
    const { submitKyc, currentUser } = useRWAStore();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const handleNext = () => {
        if (step < 3) {
            setStep(step + 1);
        } else {
            setLoading(true);
            setTimeout(() => {
                submitKyc();
                setLoading(false);
            }, 1500);
        }
    };

    if (currentUser?.kycStatus === 'pending') {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-6 animate-pulse">
                    <AlertCircle className="w-8 h-8 text-amber-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Verification Pending</h2>
                <p className="text-neutral-400 max-w-sm mb-8">
                    Your documents are currently under review by our compliance team.
                    This usually takes 24-48 hours.
                </p>
                <div className="p-4 bg-neutral-900 border border-neutral-800 rounded-lg text-sm text-neutral-500">
                    Reference ID: <span className="font-mono text-white">KYC-{currentUser.id.toUpperCase()}</span>
                </div>
            </div>
        )
    }

    return (
        <div className="max-w-xl mx-auto p-6">
            <header className="mb-8">
                <h2 className="text-2xl font-bold">Identity Verification</h2>
                <p className="text-neutral-400">Complete KYC to unlock wallet features.</p>
            </header>

            {/* STEPS INDICATOR */}
            <div className="flex items-center gap-2 mb-8">
                {[1, 2, 3].map(i => (
                    <div key={i} className={`flex-1 h-1 rounded-full ${i <= step ? 'bg-emerald-500' : 'bg-neutral-800'}`} />
                ))}
            </div>

            <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8 min-h-[400px] relative overflow-hidden">
                {step === 1 && (
                    <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="space-y-4">
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <UserIcon /> Personal Information
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs text-neutral-500">First Name</label>
                                <input className="w-full bg-neutral-950 border border-neutral-800 rounded-lg p-3" defaultValue={currentUser?.name?.split(' ')[0]} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-neutral-500">Last Name</label>
                                <input className="w-full bg-neutral-950 border border-neutral-800 rounded-lg p-3" defaultValue={currentUser?.name?.split(' ')[1]} />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs text-neutral-500">Residential Address</label>
                            <input className="w-full bg-neutral-950 border border-neutral-800 rounded-lg p-3" placeholder="Street Address" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs text-neutral-500">Date of Birth</label>
                            <input type="date" className="w-full bg-neutral-950 border border-neutral-800 rounded-lg p-3" />
                        </div>
                    </motion.div>
                )}

                {step === 2 && (
                    <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="space-y-6">
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <UploadIcon /> Document Upload
                        </h3>
                        <div className="border-2 border-dashed border-neutral-800 rounded-xl p-8 text-center hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors cursor-pointer group">
                            <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-500/20">
                                <Upload className="w-6 h-6 text-neutral-400 group-hover:text-emerald-500" />
                            </div>
                            <div className="font-medium mb-1">Upload Passport or ID</div>
                            <div className="text-xs text-neutral-500">JPG, PNG or PDF (Max 5MB)</div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-neutral-950 rounded-lg border border-neutral-800 opacity-60">
                            <div className="w-8 h-8 bg-neutral-800 rounded flex items-center justify-center">
                                <span className="text-xs font-bold">ID</span>
                            </div>
                            <span className="text-sm text-neutral-300">passport_scan_2024.jpg</span>
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto" />
                        </div>
                    </motion.div>
                )}

                {step === 3 && (
                    <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="space-y-6 text-center">
                        <h3 className="text-lg font-bold flex justify-center items-center gap-2">
                            <CameraIcon /> Liveness Check
                        </h3>
                        <div className="w-48 h-48 bg-black rounded-full mx-auto border-4 border-neutral-800 flex items-center justify-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-neutral-900 animate-pulse" />
                            <Camera className="w-8 h-8 text-neutral-600 relative z-10" />
                        </div>
                        <p className="text-sm text-neutral-400">
                            Please look directly at the camera and slowly turn your head to the left.
                        </p>
                    </motion.div>
                )}
            </div>

            <div className="mt-8 flex justify-end">
                <button
                    onClick={handleNext}
                    disabled={loading}
                    className="bg-emerald-500 text-black font-bold px-8 py-3 rounded-lg hover:bg-emerald-400 transition-colors flex items-center gap-2"
                >
                    {loading ? 'Submitting...' : step === 3 ? 'Complete Verification' : 'Continue'}
                </button>
            </div>
        </div>
    );
}

// Icons (Simple wrappers)
const UserIcon = () => <ShieldCheck className="w-5 h-5 text-emerald-500" />
const UploadIcon = () => <Upload className="w-5 h-5 text-emerald-500" />
const CameraIcon = () => <Camera className="w-5 h-5 text-emerald-500" />
